import { sampleAnim } from '@/core/animation';
import { InputController } from '@/core/input';
import { GameLoop } from '@/core/loop';
import { Rng } from '@/core/rng';
import { makePose, poseFromEulers, updatePose, type Pose } from '@/core/skeleton';
import { FrameTimer, type FrameStats } from '@/core/time';
import { combatantFromHero, heroById, type HeroTemplate } from '@/data/heroes';
import { parseZone } from '@/data/zone-schema';
import * as stoneAgeZoneJson from '@/data/zones/stone-age.json';
import {
  createBattle,
  executeMove,
  type BattleEvent,
  type BattleState,
  type Combatant,
} from '@/game/battle';
import { createCharacter } from '@/game/character';
import { endEncounter, startEncounter, type EncounterScene } from '@/game/encounter';
import { shortestAngleDelta, slideAgainstTrees } from '@/game/movement';
import { createNpc, respawnNpc, updateNpc, type Npc } from '@/game/npc';
import { createParty, leader, promoteToLeader, recruit, type Party } from '@/game/party';
import { IsoCamera, OrbitCamera, type Camera } from '@/gfx/camera';
import { Renderer, type SkinnedItem, type SunLight } from '@/gfx/renderer';
import { m4, m4Compose, type Mat4 } from '@/math/mat4';
import { v3, v3Normalize, v3Set } from '@/math/vec3';
import { createCharacterMesh } from '@/mesh/character';
import { IDLE_ANIM, WALK_ANIM } from '@/mesh/character-anims';
import { TexgenPool } from '@/texgen/worker/pool';
import { applySave, buildSave, loadSave, writeSave } from './save';
import { BattleHud } from '@/ui/battle-hud';
import { CodexPanel } from '@/ui/codex-panel';
import { Hud } from '@/ui/hud';
import { LoadingScreen } from '@/ui/loading-screen';
import { Minimap } from '@/ui/minimap';
import { PartyPanel } from '@/ui/party-panel';
import { buildZone, type ZoneRegions } from '@/world/zone';
import { type AtlasRegion } from '@/texgen/atlas/shelf';

const canvas = document.getElementById('stage');
const hudEl = document.getElementById('hud');
const battleEl = document.getElementById('battle');
const partyEl = document.getElementById('party-panel');
const codexEl = document.getElementById('codex');
const menuBtn = document.getElementById('menu-button');
const minimapEl = document.getElementById('minimap');
const loadingEl = document.getElementById('loading');
if (
  !(canvas instanceof HTMLCanvasElement) ||
  hudEl === null ||
  battleEl === null ||
  partyEl === null ||
  codexEl === null ||
  menuBtn === null ||
  !(minimapEl instanceof HTMLCanvasElement) ||
  loadingEl === null
) {
  throw new Error('boot: missing a required DOM element');
}

const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
const DPR_CAP = isCoarsePointer ? 1.0 : 1.5;
const debugMode = new URLSearchParams(window.location.search).has('debug');

const loading = new LoadingScreen(loadingEl);
const renderer = new Renderer(canvas);

const OVERWORLD_VIEW_HEIGHT = 9;
const ENCOUNTER_VIEW_HEIGHT = 3.5;

const isoCamera = new IsoCamera();
isoCamera.viewHalfHeight = OVERWORLD_VIEW_HEIGHT;
isoCamera.distance = 30;
const orbitCamera = debugMode ? new OrbitCamera() : null;
if (orbitCamera !== null) {
  orbitCamera.distance = 18;
  orbitCamera.attachInput(canvas);
}
const camera: Camera = orbitCamera ?? isoCamera;

const input = new InputController();
if (orbitCamera === null) input.attach(canvas);

const resizeCanvas = (): void => {
  const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
  const w = Math.max(1, Math.floor(window.innerWidth * dpr));
  const h = Math.max(1, Math.floor(window.innerHeight * dpr));
  if (canvas.width !== w) canvas.width = w;
  if (canvas.height !== h) canvas.height = h;
  renderer.resize(w, h);
  camera.resize(w, h);
};
resizeCanvas();
window.addEventListener('resize', resizeCanvas);
window.addEventListener('orientationchange', resizeCanvas);

const TILE_SIZE = 128;
const ATLAS_SIZE = 1024;
const WORLD_SEED = 1;

const sun: SunLight = {
  direction: v3Normalize(v3(), v3(0.55, 1.0, 0.4)),
  color: v3(1.0, 0.95, 0.85),
  skyColor: v3(0.35, 0.45, 0.58),
  groundColor: v3(0.14, 0.11, 0.08),
};

const timer = new FrameTimer(60);
const hud = new Hud(hudEl);
const battleHud = new BattleHud(battleEl);
const partyPanel = new PartyPanel(partyEl);
const codex = new CodexPanel(codexEl);
const minimap = new Minimap(minimapEl);
minimap.setVisible(true);
const party: Party = createParty();
// Lucy is the player's starting hero / overworld avatar.
recruit(party, heroById('lucy'));
partyPanel.render(party);
partyPanel.setVisible(true);

const persist = (): void => {
  void writeSave(buildSave(party)).catch((err: unknown) => {
    if (import.meta.env.DEV) console.warn('save write failed', err);
  });
};

const closeCodex = (): void => {
  codex.hide();
  menuBtn.classList.remove('hidden');
};

const openCodex = (): void => {
  codex.show(party, { onClose: closeCodex });
  menuBtn.classList.add('hidden');
};

menuBtn.addEventListener('click', () => {
  if (codex.isVisible()) closeCodex();
  else openCodex();
});
const stats: FrameStats = {
  fps: 0,
  cpuMs: 0,
  frameMs: 0,
  drawCalls: 0,
  triangles: 0,
  instances: 0,
};

if (import.meta.env.DEV) hud.setVisible(true);

window.addEventListener('keydown', (e) => {
  if (e.code === 'Backquote') {
    hud.toggle();
    e.preventDefault();
  }
});

const findRegion = (regions: readonly AtlasRegion[], id: string): AtlasRegion => {
  const r = regions.find((x) => x.id === id);
  if (r === undefined) throw new Error(`atlas region missing: ${id}`);
  return r;
};

const MOVE_SPEED = 5;
const CHARACTER_RADIUS = 0.3;
const TURN_RATE = 10;
const ENEMY_TURN_DELAY = 0.9;
const POST_BATTLE_PAUSE = 0.3;
const ENCOUNTER_TRIGGER_DIST = 0.9;
const POST_BATTLE_COOLDOWN = 1.5;
const ENEMY_RESPAWN_SECONDS = 90;
const tmpForward = v3();
const tmpRight = v3();

const leaderTemplate = (): HeroTemplate => {
  const head = leader(party);
  if (head === null) throw new Error('party has no leader');
  return heroById(head.templateId);
};

const playerCombatant = (): Combatant => combatantFromHero(leaderTemplate());

const opponentStats = (template: HeroTemplate): Combatant => combatantFromHero(template);

const boot = async (): Promise<void> => {
  loading.setMessage('Generating textures…');
  const pool = new TexgenPool();
  const atlas = await pool.generateAtlas(ATLAS_SIZE, [
    { id: 'grass.stone-age', tileSize: TILE_SIZE, seed: WORLD_SEED },
    { id: 'bark.stone-age', tileSize: TILE_SIZE, seed: WORLD_SEED },
    { id: 'stone.stone-age', tileSize: TILE_SIZE, seed: WORLD_SEED },
    { id: 'leaf.stone-age', tileSize: TILE_SIZE, seed: WORLD_SEED },
    { id: 'skin.stone-age', tileSize: TILE_SIZE, seed: WORLD_SEED },
    { id: 'dirt.stone-age', tileSize: TILE_SIZE, seed: WORLD_SEED },
  ]);
  pool.dispose();
  if (import.meta.env.DEV) {
    console.warn(`texgen: ${atlas.durationMs.toFixed(1)} ms, ${atlas.regions.length} tiles`);
  }
  const gpuAtlas = renderer.uploadAtlas(atlas.buffer, atlas.atlasSize);

  const regions: ZoneRegions = {
    grass: findRegion(atlas.regions, 'grass.stone-age'),
    bark: findRegion(atlas.regions, 'bark.stone-age'),
    leaf: findRegion(atlas.regions, 'leaf.stone-age'),
    stone: findRegion(atlas.regions, 'stone.stone-age'),
    dirt: findRegion(atlas.regions, 'dirt.stone-age'),
  };

  const worldRng = new Rng(WORLD_SEED).derive('grove');
  const zone = parseZone(stoneAgeZoneJson);
  const world = buildZone(renderer, regions, zone, worldRng);
  minimap.fitToWorld(world);

  const characterData = createCharacterMesh();
  const characterGpu = renderer.uploadMesh(characterData.mesh);
  const characterPose = makePose(characterData.skeleton);
  const characterEulers = new Float32Array(characterData.skeleton.bones.length * 3);
  updatePose(characterData.skeleton, characterPose);

  const skinRegion = findRegion(atlas.regions, 'skin.stone-age');
  const characterUv = new Float32Array([
    skinRegion.u0,
    skinRegion.v0,
    skinRegion.u1 - skinRegion.u0,
    skinRegion.v1 - skinRegion.v0,
  ]);
  const CHARACTER_Y_LIFT = 0.03;
  let characterScale = 1.3;
  const character = createCharacter(world.spawn.x, world.spawn.z, Math.PI / 2);
  const characterModel = m4();
  const characterTint = v3(1, 1, 1);
  const characterItem: SkinnedItem = {
    mesh: characterGpu,
    model: characterModel,
    uvRect: characterUv,
    tint: characterTint,
    palette: characterPose.palette,
  };

  const refreshLeaderVisual = (): void => {
    const t = leaderTemplate();
    characterTint[0] = t.visual.tintR;
    characterTint[1] = t.visual.tintG;
    characterTint[2] = t.visual.tintB;
    characterScale = t.visual.scale;
  };
  refreshLeaderVisual();

  const npcRngBase = new Rng(WORLD_SEED).derive('npcs');
  const npcs: Npc[] = [];
  const npcPoses: Pose[] = [];
  const npcEulers: Float32Array[] = [];
  const npcModels: Mat4[] = [];
  const npcScales: number[] = [];
  const npcItems: SkinnedItem[] = [];

  for (let i = 0; i < zone.npcs.length; i++) {
    const entry = zone.npcs[i]!;
    const template = heroById(entry.templateId);
    const opts: Parameters<typeof createNpc>[3] = {
      startAt: { x: entry.x, z: entry.z },
    };
    if (entry.behavior === 'patrol' && entry.patrol !== undefined && entry.patrol.length > 0) {
      opts.patrol = entry.patrol;
    }
    const npc = createNpc(
      entry.templateId,
      npcRngBase.derive(`npc:${entry.templateId}`),
      world.bounds,
      opts,
    );
    npcs.push(npc);
    const pose = makePose(characterData.skeleton);
    updatePose(characterData.skeleton, pose);
    npcPoses.push(pose);
    npcEulers.push(new Float32Array(characterData.skeleton.bones.length * 3));
    npcScales.push(template.visual.scale);
    const model = m4();
    m4Compose(model, npc.x, CHARACTER_Y_LIFT, npc.z, npc.yaw, template.visual.scale);
    npcModels.push(model);
    npcItems.push({
      mesh: characterGpu,
      model,
      uvRect: characterUv,
      tint: v3(template.visual.tintR, template.visual.tintG, template.visual.tintB),
      palette: pose.palette,
    });
  }

  const rebuildOverworldItems = (): void => {
    overworldSkinnedItems.length = 1;
    for (let i = 0; i < npcs.length; i++) {
      const n = npcs[i]!;
      if (n.recruited || n.defeated) continue;
      overworldSkinnedItems.push(npcItems[i]!);
    }
  };
  const overworldSkinnedItems: SkinnedItem[] = [characterItem];

  // Any hero recruited at module init (default Lucy) needs to mark their
  // NPC counterpart in the grove as recruited so they don't appear as wild.
  for (const m of party.recruited) {
    const idx = npcs.findIndex((n) => n.templateId === m.templateId);
    if (idx !== -1) npcs[idx]!.recruited = true;
  }
  rebuildOverworldItems();

  const save = await loadSave();
  if (save !== null) {
    applySave(save, party, npcs);
    partyPanel.render(party);
    rebuildOverworldItems();
    refreshLeaderVisual();
    if (import.meta.env.DEV) {
      console.warn(`save loaded: party=${party.recruited.length}`);
    }
  }

  partyPanel.setOnPromote((templateId) => {
    if (mode !== 'overworld') return;
    if (promoteToLeader(party, templateId)) {
      refreshLeaderVisual();
      partyPanel.render(party);
      persist();
    }
  });

  let mode: 'overworld' | 'encounter' = 'overworld';
  let battle: BattleState | null = null;
  let scene: EncounterScene | null = null;
  let enemyTurnTimer = 0;
  let lastSeenLogLen = 0;
  let postBattleTimer = 0;
  let encounterCooldown = 0;
  let recruitChoiceShown = false;
  const battleRngRoot = new Rng(WORLD_SEED).derive('battles');
  let battleId = 0;

  const enterEncounter = (npcIdx: number): void => {
    const opponent = npcs[npcIdx]!;
    const template = heroById(opponent.templateId);
    scene = startEncounter(character, npcs, npcIdx);
    battle = createBattle(
      playerCombatant(),
      opponentStats(template),
      battleRngRoot.derive(`battle:${battleId++}`),
    );
    lastSeenLogLen = 0;
    enemyTurnTimer = ENEMY_TURN_DELAY;
    postBattleTimer = 0;
    recruitChoiceShown = false;
    pendingPlayerMoveIdx = null;
    movesShown = false;
    isoCamera.viewHalfHeight = ENCOUNTER_VIEW_HEIGHT;
    isoCamera.resize(canvas.width, canvas.height);
    battleHud.show(battle);
    partyPanel.setVisible(false);
    minimap.setVisible(false);
    menuBtn.classList.add('hidden');
    if (codex.isVisible()) closeCodex();
    mode = 'encounter';
  };

  const leaveEncounter = (recruitWinner: boolean): void => {
    if (scene === null || battle === null) return;
    const opponentIdx = scene.opponentNpcIndex;
    if (battle.status === 'end-victory') {
      const opponent = npcs[opponentIdx]!;
      if (recruitWinner) {
        const template = heroById(opponent.templateId);
        recruit(party, template);
        opponent.recruited = true;
        partyPanel.render(party);
      } else {
        // Defeated but not recruited: hide them and start the respawn clock.
        opponent.defeated = true;
        opponent.respawnIn = ENEMY_RESPAWN_SECONDS;
      }
      rebuildOverworldItems();
    }
    endEncounter(scene, character, npcs);
    scene = null;
    battle = null;
    isoCamera.viewHalfHeight = OVERWORLD_VIEW_HEIGHT;
    isoCamera.resize(canvas.width, canvas.height);
    battleHud.hide();
    partyPanel.setVisible(true);
    minimap.setVisible(true);
    menuBtn.classList.remove('hidden');
    encounterCooldown = POST_BATTLE_COOLDOWN;
    mode = 'overworld';
    persist();
  };

  const flushBattleEvents = (state: BattleState): void => {
    for (let i = lastSeenLogLen; i < state.log.length; i++) {
      const ev: BattleEvent = state.log[i]!;
      if (ev.kind === 'damage') battleHud.spawnDamage(ev.target, ev.amount);
      else if (ev.kind === 'miss') battleHud.spawnMiss(ev.target);
    }
    lastSeenLogLen = state.log.length;
    battleHud.update(state);
  };

  const updateOverworld = (dtSec: number): void => {
    const az = isoCamera.azimuth;
    v3Set(tmpForward, -Math.cos(az), 0, -Math.sin(az));
    v3Set(tmpRight, Math.sin(az), 0, -Math.cos(az));

    const moveX = input.state.moveX;
    const moveZ = input.state.moveZ;
    const fx = tmpForward[0]!;
    const fz = tmpForward[2]!;
    const rx = tmpRight[0]!;
    const rz = tmpRight[2]!;

    const vx = rx * moveX + fx * -moveZ;
    const vz = rz * moveX + fz * -moveZ;
    const speed = Math.hypot(vx, vz);
    const moving = speed > 0.05;

    if (moving) {
      let nx = character.x + vx * MOVE_SPEED * dtSec;
      let nz = character.z + vz * MOVE_SPEED * dtSec;

      const b = world.bounds;
      if (nx < b.minX + CHARACTER_RADIUS) nx = b.minX + CHARACTER_RADIUS;
      else if (nx > b.maxX - CHARACTER_RADIUS) nx = b.maxX - CHARACTER_RADIUS;
      if (nz < b.minZ + CHARACTER_RADIUS) nz = b.minZ + CHARACTER_RADIUS;
      else if (nz > b.maxZ - CHARACTER_RADIUS) nz = b.maxZ - CHARACTER_RADIUS;

      const [cx, cz] = slideAgainstTrees(world.colliders, character.x, character.z, nx, nz, CHARACTER_RADIUS);
      character.x = cx;
      character.z = cz;

      const targetYaw = Math.atan2(vx, -vz);
      const dyaw = shortestAngleDelta(targetYaw, character.yaw);
      const maxStep = TURN_RATE * dtSec;
      character.yaw += Math.abs(dyaw) > maxStep ? Math.sign(dyaw) * maxStep : dyaw;
    }

    isoCamera.target[0] = character.x;
    isoCamera.target[2] = character.z;

    character.animTime += dtSec;
    const anim = moving ? WALK_ANIM : IDLE_ANIM;
    characterEulers.fill(0);
    sampleAnim(anim, character.animTime, characterEulers);
    poseFromEulers(characterData.skeleton, characterPose, characterEulers);
    updatePose(characterData.skeleton, characterPose);
    m4Compose(characterModel, character.x, CHARACTER_Y_LIFT, character.z, character.yaw, characterScale);

    for (let i = 0; i < npcs.length; i++) {
      const npc = npcs[i]!;
      if (npc.recruited) continue;
      if (npc.defeated) {
        npc.respawnIn -= dtSec;
        if (npc.respawnIn <= 0) {
          respawnNpc(npc, world.bounds, world.colliders);
          rebuildOverworldItems();
        }
        continue;
      }
      const npcMoving = updateNpc(npc, dtSec, world.colliders, world.bounds);
      const eulers = npcEulers[i]!;
      eulers.fill(0);
      sampleAnim(npcMoving ? WALK_ANIM : IDLE_ANIM, npc.animTime, eulers);
      poseFromEulers(characterData.skeleton, npcPoses[i]!, eulers);
      updatePose(characterData.skeleton, npcPoses[i]!);
      m4Compose(npcModels[i]!, npc.x, CHARACTER_Y_LIFT, npc.z, npc.yaw, npcScales[i]!);
    }

    if (encounterCooldown > 0) {
      encounterCooldown -= dtSec;
    } else {
      const trigger = ENCOUNTER_TRIGGER_DIST;
      const triggerSq = trigger * trigger;
      for (let i = 0; i < npcs.length; i++) {
        const n = npcs[i]!;
        if (n.recruited || n.defeated) continue;
        const dx = n.x - character.x;
        const dz = n.z - character.z;
        if (dx * dx + dz * dz < triggerSq) {
          enterEncounter(i);
          return;
        }
      }
    }
  };

  let pendingPlayerMoveIdx: number | null = null;
  let movesShown = false;

  const updateEncounter = (dtSec: number): void => {
    if (battle === null || scene === null) return;

    if (battle.status === 'pending-action') {
      if (battle.whoseTurn === 'player') {
        if (!movesShown) {
          movesShown = true;
          battleHud.showMoveChoices(battle.player.moves, (idx) => {
            pendingPlayerMoveIdx = idx;
          });
        }
        if (pendingPlayerMoveIdx !== null) {
          const idx = pendingPlayerMoveIdx;
          pendingPlayerMoveIdx = null;
          movesShown = false;
          battleHud.hideChoices();
          battle = executeMove(battle, idx);
          flushBattleEvents(battle);
          enemyTurnTimer = ENEMY_TURN_DELAY;
        }
      } else {
        movesShown = false;
        enemyTurnTimer -= dtSec;
        if (enemyTurnTimer <= 0) {
          const enemyMoves = battle.enemy.moves;
          const enemyIdx = enemyMoves.length > 1 ? battle.rng.int(0, enemyMoves.length) : 0;
          battle = executeMove(battle, enemyIdx);
          flushBattleEvents(battle);
        }
      }
    } else if (battle.status === 'end-victory') {
      postBattleTimer += dtSec;
      if (!recruitChoiceShown && postBattleTimer >= POST_BATTLE_PAUSE) {
        recruitChoiceShown = true;
        battleHud.showRecruitChoice((choice) => {
          leaveEncounter(choice === 'recruit');
        });
      }
    } else {
      postBattleTimer += dtSec;
      if (postBattleTimer >= POST_BATTLE_PAUSE && input.state.confirmPressed) {
        leaveEncounter(false);
        return;
      }
    }

    // Pose both combatants on idle, animation time runs continuously.
    character.animTime += dtSec;
    characterEulers.fill(0);
    sampleAnim(IDLE_ANIM, character.animTime, characterEulers);
    poseFromEulers(characterData.skeleton, characterPose, characterEulers);
    updatePose(characterData.skeleton, characterPose);
    m4Compose(characterModel, character.x, CHARACTER_Y_LIFT, character.z, character.yaw, characterScale);

    const opponentIdx = scene.opponentNpcIndex;
    const opponent = npcs[opponentIdx]!;
    opponent.animTime += dtSec;
    const oppEulers = npcEulers[opponentIdx]!;
    oppEulers.fill(0);
    sampleAnim(IDLE_ANIM, opponent.animTime, oppEulers);
    poseFromEulers(characterData.skeleton, npcPoses[opponentIdx]!, oppEulers);
    updatePose(characterData.skeleton, npcPoses[opponentIdx]!);
    m4Compose(npcModels[opponentIdx]!, opponent.x, CHARACTER_Y_LIFT, opponent.z, opponent.yaw, npcScales[opponentIdx]!);

    isoCamera.target[0] = scene.arenaX;
    isoCamera.target[2] = scene.arenaZ;
  };

  const update = (dtSec: number): void => {
    input.poll();
    if (codex.isVisible()) {
      if (input.state.cancelPressed || input.state.menuPressed) closeCodex();
      return;
    }
    if (mode === 'overworld') {
      if (input.state.menuPressed) {
        openCodex();
        return;
      }
      updateOverworld(dtSec);
    } else {
      updateEncounter(dtSec);
    }
  };

  const encounterSkinnedItems: SkinnedItem[] = [characterItem];

  const render = (_alpha: number, frameDtSec: number): void => {
    const cpuStart = performance.now();
    camera.update(frameDtSec);
    if (mode === 'overworld') {
      renderer.render(camera, sun, gpuAtlas, [], world.batches, overworldSkinnedItems);
    } else if (scene !== null) {
      encounterSkinnedItems[1] = npcItems[scene.opponentNpcIndex]!;
      renderer.render(camera, sun, gpuAtlas, [], world.batches, encounterSkinnedItems);
    }
    const cpuMs = performance.now() - cpuStart;
    timer.push(frameDtSec * 1000, cpuMs);
    timer.read(stats);
    stats.drawCalls = renderer.stats.drawCalls;
    stats.triangles = renderer.stats.triangles;
    stats.instances = renderer.stats.instances;
    const now = performance.now();
    hud.render(now, stats);
    if (mode === 'overworld') minimap.render(character, npcs, world, now);
  };

  const loop = new GameLoop(update, render, { fixedHz: 60, maxFrameSkipMs: 100 });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      loop.stop();
      persist();
    } else {
      loop.start();
    }
  });

  window.addEventListener('pagehide', () => persist());

  loading.hide();
  loop.start();
};

void boot();
